import { useCallback, useContext } from 'react';
import { UserContext } from '../../../UserContext';
import type { FeatureName, SecurityAction, SecurityDecision, SecurityDecisionBatch, SecurityTarget } from '../../types';
import { FeatureSecurityContext } from '../contexts/FeatureSecurityContext';
import { evaluateSecurityDecisions, type SecurityEvaluationContext } from './securityEvaluationUtils';

type FeatureSecurityApplication = {
    featureApps: string[];
};

type UseFeatureSecurityEvaluatorOptions = {
    /** Override `UserContext.application` — used by AppRoute while context catches up on app switch. */
    application?: FeatureSecurityApplication;
};

type FeatureSecurityTarget = Pick<SecurityTarget, 'featureName'> & {
    featureName: FeatureName;
};

export type FeatureSecurityEvaluator = {
    (action: SecurityAction, featureTarget: FeatureSecurityTarget): SecurityDecision;
    (actions: SecurityAction[], featureTarget: FeatureSecurityTarget): SecurityDecisionBatch;
};

/**
 * Lazy/imperative security evaluator for per-call feature targets.
 *
 * Unlike `useSecurity` (eager, one shared target), this returns a callback so each
 * invocation can pass its own feature target — useful for route gates and drawer
 * visibility across many route features. Supports optional `application` override
 * when evaluation must use the route app instead of context. Supports single-action
 * and batched overloads.
 */
export function useFeatureSecurityEvaluator(
    options: UseFeatureSecurityEvaluatorOptions = {}
): FeatureSecurityEvaluator {
    const { application: applicationOverride } = options;
    const { userProfile, application, activeOrganizationAccount } = useContext(UserContext);
    const featureContextValue = useContext(FeatureSecurityContext);

    const evaluator = useCallback(
        (actionOrActions: SecurityAction | SecurityAction[], featureTarget: FeatureSecurityTarget) => {
            const featureNames = featureContextValue?.featureNames ?? [];
            const evaluationContext: SecurityEvaluationContext = {
                userProfile,
                application: applicationOverride ?? application,
                activeOrganizationAccount,
                featureNames,
                entitySecurityContexts: []
            };

            return evaluateSecurityDecisions(actionOrActions, featureTarget, evaluationContext);
        },
        [
            activeOrganizationAccount,
            application,
            applicationOverride,
            featureContextValue?.featureNames,
            userProfile
        ]
    ) as FeatureSecurityEvaluator;

    return evaluator;
}
